import React, { useState, useEffect, useCallback } from 'react';
import { API_BASE_URL } from '../../config';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Alert,
  Tooltip,
  Chip
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useTranslation } from '../../utils/localization/localization';
import { tableShellSx, responsiveTableContainerSx } from '../common';

const SkillManagement = () => {
  const { t } = useTranslation();

  // State management
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentCategory, setCurrentCategory] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const skillTableColumnSx = {
    name: { width: { xs: '20%', md: '20%' }, minWidth: { xs: 140, md: 180 } },
    description: { width: { xs: '36%', md: '40%' }, minWidth: { xs: 220, md: 320 } },
    order: { width: { xs: '9%', md: '9%' }, minWidth: { xs: 70, md: 90 }, whiteSpace: 'nowrap' },
    status: { width: { xs: '12%', md: '12%' }, minWidth: { xs: 90, md: 110 }, whiteSpace: 'nowrap' },
    created: { width: { xs: '13%', md: '11%' }, minWidth: { xs: 100, md: 120 }, whiteSpace: 'nowrap' },
    actions: { width: { xs: '1%', md: '8%' }, minWidth: 96, whiteSpace: 'nowrap' }
  };

  // Form data
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    display_order: 0,
    is_active: true
  });

  // Load categories from API
  const loadCategories = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/system/categories`);
      const data = await response.json();

      if (response.ok) {
        setCategories(data.result || []);
      } else {
        throw new Error(data.detail || 'Failed to load categories');
      }
    } catch (error) {
      setError(t('admin.skills.loadError') || 'Failed to load skill categories');
      setCategories([]);
    } finally {
      setLoading(false);
    }
  }, [t]);

  // Add or update category
  const saveCategory = async () => {
    setError('');
    setSuccess('');
    try {
      const isEdit = editMode && currentCategory?.id;

      const url = isEdit
        ? `${API_BASE_URL}/system/categories/${currentCategory.id}`
        : `${API_BASE_URL}/system/categories`;

      const formDataObj = new FormData();
      formDataObj.append('name', formData.name);
      formDataObj.append('description', formData.description);
      formDataObj.append('category_type', 'all');
      formDataObj.append('display_order', formData.display_order.toString());
      if (isEdit) {
        formDataObj.append('is_active', formData.is_active.toString());
      }

      const response = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        body: formDataObj
      });

      const result = await response.json();

      if (response.ok) {
        setSuccess(
          isEdit
            ? (t('admin.skills.updateSuccess') || 'Category updated successfully')
            : (t('admin.skills.createSuccess') || 'Category created successfully')
        );
        setDialogOpen(false);
        resetForm();
        await loadCategories();
      } else {
        throw new Error(result.detail || 'Failed to save category');
      }
    } catch (error) {
      setError(t('admin.skills.saveError') || 'Failed to save category');
    }
  };

  // Delete category
  const deleteCategory = async (categoryId, categoryName) => {
    if (!window.confirm(t('admin.skills.confirmDelete') || `Are you sure you want to delete "${categoryName}"?`)) {
      return;
    }

    setError('');
    setSuccess('');
    try {
      const response = await fetch(`${API_BASE_URL}/system/categories/${categoryId}`, {
        method: 'DELETE'
      });

      const result = await response.json();

      if (response.ok) {
        setSuccess(t('admin.skills.deleteSuccess') || 'Category deleted successfully');
        await loadCategories();
      } else {
        throw new Error(result.detail || 'Failed to delete category');
      }
    } catch (error) {
      setError(t('admin.skills.deleteError') || 'Failed to delete category');
    }
  };

  // Form management
  const openAddDialog = () => {
    setEditMode(false);
    setCurrentCategory(null);
    resetForm();
    setDialogOpen(true);
  };

  const openEditDialog = (category) => {
    setEditMode(true);
    setCurrentCategory(category);
    setFormData({
      name: category.name,
      description: category.description || '',
      display_order: category.display_order || 0,
      is_active: category.is_active
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      display_order: 0,
      is_active: true
    });
  };

  const handleFormChange = (field) => (event) => {
    const value = field === 'is_active' ? event.target.checked : event.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Load categories on component mount
  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h2">
          {t('admin.skills.title') || 'Skill Categories Management'}
        </Typography>
        <Button
          startIcon={<RefreshIcon />}
          onClick={loadCategories}
          disabled={loading}
        >
          {t('common.refresh') || 'Refresh'}
        </Button>
      </Box>

      {/* Success Alert */}
      {success && (
        <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {/* Error Alert */}
      {error && (
        <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Description */}
      <Alert severity="info" sx={{ mb: 3 }}>
        {t('admin.skills.description') ||
         'Manage skill categories used in CV analyzer and job analyzer. These categories help organize and categorize competencies and skills.'}
      </Alert>

      {/* Categories Table */}
      <Box sx={tableShellSx}>
        <TableContainer component={Paper} sx={responsiveTableContainerSx}>
          <Table sx={{ tableLayout: 'fixed', minWidth: { xs: 860, md: 1020 } }}>
            <TableHead>
              <TableRow>
                <TableCell sx={skillTableColumnSx.name}>{t('admin.skills.name') || 'Name'}</TableCell>
                <TableCell sx={skillTableColumnSx.description}>{t('admin.skills.descriptionLabel') || 'Description'}</TableCell>
                <TableCell sx={skillTableColumnSx.order}>{t('admin.skills.order') || 'Order'}</TableCell>
                <TableCell sx={skillTableColumnSx.status}>{t('admin.skills.status') || 'Status'}</TableCell>
                <TableCell sx={skillTableColumnSx.created}>{t('admin.skills.created') || 'Created'}</TableCell>
                <TableCell className="table-actions-cell" sx={skillTableColumnSx.actions}>{t('common.actions') || 'Actions'}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} sx={{ textAlign: 'center', py: 3 }}>
                    {t('common.loading') || 'Loading...'}
                  </TableCell>
                </TableRow>
              ) : categories.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} sx={{ textAlign: 'center', py: 3 }}>
                    {t('admin.skills.noCategories') || 'No skill categories found'}
                  </TableCell>
                </TableRow>
              ) : (
                categories.map((category) => (
                  <TableRow key={category.id} hover>
                    <TableCell sx={skillTableColumnSx.name}>
                      <Typography variant="body2" fontWeight="medium">
                        {category.name}
                      </Typography>
                    </TableCell>
                    <TableCell className="table-cell-truncate" sx={skillTableColumnSx.description}>
                      <Typography variant="body2" color="textSecondary" sx={{ overflowWrap: 'anywhere' }}>
                        {category.description}
                      </Typography>
                    </TableCell>
                    <TableCell sx={skillTableColumnSx.order}>
                      <Typography variant="body2">
                        {category.display_order}
                      </Typography>
                    </TableCell>
                    <TableCell sx={skillTableColumnSx.status}>
                      <Chip
                        label={category.is_active ? (t('common.active') || 'Active') : (t('common.inactive') || 'Inactive')}
                        size="small"
                        color={category.is_active ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell sx={skillTableColumnSx.created}>
                      <Typography variant="body2" color="textSecondary">
                        {new Date(category.created_at).toLocaleDateString()}
                      </Typography>
                    </TableCell>
                    <TableCell className="table-actions-cell" sx={skillTableColumnSx.actions}>
                      <Tooltip title={t('common.edit') || 'Edit'}>
                        <IconButton
                          size="small"
                          onClick={() => openEditDialog(category)}
                          sx={{ mr: 1 }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('common.delete') || 'Delete'}>
                        <IconButton
                          size="small"
                          onClick={() => deleteCategory(category.id, category.name)}
                          color="error"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Add Category Button */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={openAddDialog}
        >
          {t('admin.skills.addCategory') || 'Add Category'}
        </Button>
      </Box>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editMode
            ? (t('admin.skills.editCategory') || 'Edit Category')
            : (t('admin.skills.addCategory') || 'Add Category')
          }
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <TextField
            autoFocus
            margin="dense"
            label={t('admin.skills.name') || 'Name'}
            fullWidth
            variant="outlined"
            value={formData.name}
            onChange={handleFormChange('name')}
            required
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label={t('admin.skills.descriptionLabel') || 'Description'}
            fullWidth
            variant="outlined"
            multiline
            rows={3}
            value={formData.description}
            onChange={handleFormChange('description')}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label={t('admin.skills.order') || 'Display Order'}
            fullWidth
            variant="outlined"
            type="number"
            value={formData.display_order}
            onChange={handleFormChange('display_order')}
            sx={{ mb: 2 }}
          />
          {editMode && (
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_active}
                  onChange={handleFormChange('is_active')}
                />
              }
              label={t('admin.skills.active') || 'Active'}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>
            {t('common.cancel') || 'Cancel'}
          </Button>
          <Button onClick={saveCategory} variant="contained" disabled={!formData.name.trim()}>
            {editMode
              ? (t('common.update') || 'Update')
              : (t('common.create') || 'Create')
            }
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SkillManagement;
